<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert :message="alertMessage" :show="alertShow" :type="alertType"></floating-alert>

		<v-card elevation="2" rounded="xl">
			<div class="frs-header">
				<div class="frs-header-left">
					<div class="frs-icon-box">
						<v-icon size="18" color="white">mdi-truck-delivery-outline</v-icon>
					</div>
					<div>
						<p class="frs-title">Fournisseurs</p>
						<p class="frs-subtitle">Gestion des fournisseurs</p>
					</div>
				</div>

				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined dense hide-details
					class="frs-search"
					color="blue darken-2"
				></v-text-field>

				<v-dialog v-model="dialog" max-width="520px">
					<template v-slot:activator="{ on, attrs }">
						<v-btn depressed rounded class="frs-add-btn" v-bind="attrs" v-on="on">
							<v-icon left small>mdi-plus</v-icon>
							Nouveau fournisseur
						</v-btn>
					</template>
					<v-card rounded="xl" class="pa-2">
						<div class="form-dialog-header">
							<div class="form-dialog-icon" style="background:#00695c;">
								<v-icon size="14" color="white">mdi-truck-delivery-outline</v-icon>
							</div>
							<div>
								<p class="form-dialog-title">{{ formTitle }}</p>
								<p class="form-dialog-sub">Fournisseur</p>
							</div>
							<v-spacer></v-spacer>
							<v-btn icon small @click="closeDialog"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
						</div>
						<v-divider></v-divider>
						<v-card-text class="pt-5">
							<v-form ref="form" v-model="valid" lazy-validation>
								<v-row>
									<v-col cols="12" sm="6">
										<v-text-field
											v-model="form.name"
											label="Nom"
											outlined dense rounded
											prepend-inner-icon="mdi-domain"
											:rules="[v => !!v || 'Champ requis']"
											hide-details="auto"
										></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field
											v-model="form.sector"
											label="Secteur"
											outlined dense rounded
											prepend-inner-icon="mdi-briefcase"
											hide-details
										></v-text-field>
									</v-col>
									<v-col cols="12">
										<v-text-field
											v-model="form.address"
											label="Adresse"
											outlined dense rounded
											prepend-inner-icon="mdi-map-marker"
											:rules="[v => !!v || 'Champ requis']"
											hide-details="auto"
										></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field
											v-model="form.phone"
											label="Téléphone (optionnel)"
											outlined dense rounded
											prepend-inner-icon="mdi-phone"
											hide-details
										></v-text-field>
									</v-col>
									<v-col cols="12" sm="6">
										<v-text-field
											v-model="form.email"
											label="Email (optionnel)"
											outlined dense rounded
											prepend-inner-icon="mdi-email"
											:rules="[v => !v || /.+@.+\..+/.test(v) || 'Email invalide']"
											hide-details="auto"
										></v-text-field>
									</v-col>
								</v-row>
							</v-form>
						</v-card-text>
						<v-card-actions class="pa-4 pt-2">
							<v-btn text rounded color="grey darken-1" @click="closeDialog">Annuler</v-btn>
							<v-spacer></v-spacer>
							<v-btn :disabled="!valid" depressed rounded class="frs-save-btn" @click="save">Enregistrer</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="suppliers"
				:server-items-length="totalItems"
				:options.sync="options"
				:loading="loading"
				loading-text="Chargement en cours..."
				class="frs-table"
				:footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Lignes par page', 'items-per-page-all-text': 'Tout' }"
			>
				<template v-slot:[`item.email`]="{ item }">
					<a v-if="item.email" :href="`mailto:${item.email}`" class="frs-email">{{ item.email }}</a>
					<span v-else class="frs-empty">—</span>
				</template>
				<template v-slot:[`item.phone`]="{ item }">
					<span v-if="item.phone">{{ item.phone }}</span>
					<span v-else class="frs-empty">—</span>
				</template>
				<template v-slot:[`item.sector`]="{ item }">
					<v-chip v-if="item.sector" small color="#f0fdf4" text-color="#065f46">{{ item.sector }}</v-chip>
					<span v-else class="frs-empty">—</span>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-btn icon small @click="editItem(item)"><v-icon size="17" color="#374151">mdi-pencil</v-icon></v-btn>
					<v-btn icon small @click="deleteItem(item)"><v-icon size="17" color="#c62828">mdi-delete</v-icon></v-btn>
				</template>
				<template v-slot:[`footer.page-text`]="items">{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer ce fournisseur ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="dialogDelete = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import service from "../services";
export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		loaderDialog: false,
		alertMessage: "",
		alertShow: false,
		alertType: "",
		dialog: false,
		dialogDelete: false,
		valid: false,
		loading: false,
		search: "",
		searchTimeout: null,
		options: {},
		totalItems: 0,
		suppliers: [],
		editedId: null,
		form: { name: "", address: "", phone: "", email: "", sector: "" },
		defaultForm: { name: "", address: "", phone: "", email: "", sector: "" },
		headers: [
			{ text: "Nom", value: "name", sortable: false },
			{ text: "Adresse", value: "address", sortable: false },
			{ text: "Téléphone", value: "phone", sortable: false },
			{ text: "Email", value: "email", sortable: false },
			{ text: "Secteur", value: "sector", sortable: false },
			{ text: "Actions", value: "actions", sortable: false, width: "100px" },
		],
	}),
	computed: {
		formTitle() { return this.editedId ? "Modifier le fournisseur" : "Nouveau fournisseur"; },
	},
	watch: {
		options: { handler() { this.initialize(); }, deep: true },
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) this.options.page = 1;
				else this.initialize();
			}, 400);
		},
		dialog(val) { val || this.closeDialog(); },
	},
	methods: {
		initialize() {
			this.loading = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getSuppliers({ page, limit, search: this.search })
				.then((r) => {
					this.suppliers = r.data?.fournisseurs || [];
					this.totalItems = (r.data?.totalPages ?? 0) * limit;
					this.loading = false;
				})
				.catch((e) => { console.log(e); this.loading = false; });
		},
		manageAlert(show, type, msg) { this.alertShow = show; this.alertType = type; this.alertMessage = msg; },
		editItem(item) {
			this.editedId = item._id;
			this.form = { name: item.name, address: item.address || "", phone: item.phone || "", email: item.email || "", sector: item.sector || "" };
			this.dialog = true;
		},
		deleteItem(item) { this.editedId = item._id; this.dialogDelete = true; },
		deleteConfirm() {
			this.loaderDialog = true;
			service.deleteSupplier(this.editedId)
				.then(() => { this.loaderDialog = false; this.dialogDelete = false; this.editedId = null; this.initialize(); })
				.catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
		closeDialog() {
			this.dialog = false;
			this.$nextTick(() => { this.form = Object.assign({}, this.defaultForm); this.editedId = null; if (this.$refs.form) this.$refs.form.resetValidation(); });
		},
		save() {
			if (!this.$refs.form.validate()) return;
			this.loaderDialog = true;
			const p = this.editedId
				? service.updateSupplier(this.editedId, this.form)
				: service.createSupplier(this.form);
			p.then(() => {
				this.loaderDialog = false;
				this.manageAlert(true, "success", "Fournisseur enregistré !");
				this.closeDialog();
				this.initialize();
			}).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
	},
};
</script>

<style scoped>
.frs-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.frs-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.frs-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #00695c; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.frs-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.frs-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.frs-search { max-width: 240px; }
.frs-add-btn { background: #00695c !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(0,105,92,0.28) !important; letter-spacing: 0.3px; }
.frs-table { font-family: Montserrat, sans-serif; }
.frs-email { color: #1565c0; text-decoration: none; font-weight: 500; }
.frs-email:hover { text-decoration: underline; }
.frs-empty { color: #d1d5db; }
.form-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.form-dialog-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.form-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.form-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.frs-save-btn { background: #00695c !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(0,105,92,0.28) !important; letter-spacing: 0.3px; }
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.del-confirm-btn { background: #b91c1c !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 3px 10px rgba(185,28,28,0.3) !important; }
</style>
